import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Music, X } from "lucide-react";
import { sr } from "../../lib/sr";

/** Attach a client-owned background music bed to a video/CTV creative. The bed is auto-ducked
 * under the narration when the montage is (re)built. */
export default function MusicControl({ planId, channel, creative, onUpdate, vol, onVol, disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const has = !!creative.music_file_id;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("channel", channel);
    fd.append("volume", String(vol ?? creative.music_volume ?? 0.5));
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await sr.uploadCreativeMusic(planId, fd);
      onUpdate(r.growth_plan);
      toast.success(`Music added — rebuild the combined video to hear it`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add music");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await sr.clearCreativeMusic(planId, channel);
      onUpdate(r.growth_plan);
      toast.success("Music removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove music");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid={`music-control-${channel}`}>
      <input ref={inputRef} type="file" accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg" onChange={onFile} style={{ display: "none" }} data-testid={`music-file-${channel}`} />
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => inputRef.current?.click()} disabled={busy || disabled} data-testid={`music-add-${channel}`}>
        {busy ? <Loader2 size={13} className="sr-spin" /> : <Music size={13} />} {has ? "Replace music" : "Add music bed"}
      </button>
      {has && (
        <>
          <span className="sr-muted" style={{ fontSize: 11.5, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={creative.music_name} data-testid={`music-name-${channel}`}>♫ {creative.music_name}</span>
          <label className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }}>
            Vol
            <input type="range" min="0" max="1" step="0.05" value={vol ?? creative.music_volume ?? 0.5} onChange={(e) => onVol(parseFloat(e.target.value))} data-testid={`music-vol-${channel}`} style={{ width: 68 }} />
          </label>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={remove} disabled={busy || disabled} data-testid={`music-remove-${channel}`}><X size={12} /></button>
        </>
      )}
    </div>
  );
}
